import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Home } from "lucide-react";
import { Button } from "../components/ui/button";

const Login = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [rememberDevice, setRememberDevice] = useState(
    () => localStorage.getItem("remember_device") === "true"
  );

  useEffect(() => {
    if (user && !loading) {
      navigate("/dashboard");
    }
  }, [user, loading, navigate]);

  const handleGoogleLogin = () => {
    localStorage.setItem("remember_device", rememberDevice ? "true" : "false");
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--t-bg)' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--t-primary)' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Login Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16" style={{ background: 'var(--t-bg)' }}>
        <div className="max-w-md mx-auto w-full">
          {/* Logo */}
          <div className="mb-10">
            <img 
              src="https://customer-assets.emergentagent.com/job_contract-flow-21/artifacts/cuiv7qxm_eo-logo-blue-stacked-header-logo.png"
              alt="EasyOffice"
              className="h-12 w-auto"
              data-testid="login-logo"
            />
          </div>

          {/* Welcome Text */}
          <h1 className="text-4xl font-semibold tracking-tight mb-3" style={{ fontFamily: 'var(--t-heading-font)', color: 'var(--t-text)' }}>
            Transaction Manager
          </h1>
          <p className="text-base mb-10" style={{ color: 'var(--t-text-muted)', fontFamily: 'var(--t-body-font)' }}>
            Manage your real estate transactions, deadlines, and workflows all in one place.
          </p>

          {/* Login Button */}
          <Button
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
            className="w-full py-3.5 text-base font-medium flex items-center justify-center gap-3 rounded-xl transition-all"
            style={{ background: 'var(--t-primary)', color: '#FFF', border: 'none' }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--t-primary-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'var(--t-primary)'}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Sign in with Google
          </Button>

          {/* Remember Device Toggle */}
          <label
            className="flex items-center gap-2.5 mt-5 cursor-pointer select-none group"
            data-testid="remember-device-label"
          >
            <button
              type="button"
              role="switch"
              aria-checked={rememberDevice}
              data-testid="remember-device-toggle"
              onClick={() => setRememberDevice(v => !v)}
              className="relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{
                background: rememberDevice ? 'var(--t-primary)' : 'var(--t-border)',
                focusVisibleRingColor: 'var(--t-primary)',
              }}
            >
              <span
                className="pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transform transition-transform duration-200"
                style={{
                  transform: rememberDevice ? 'translateX(17px) translateY(2px)' : 'translateX(2px) translateY(2px)',
                }}
              />
            </button>
            <span className="text-sm" style={{ color: 'var(--t-text-muted)' }}>
              Remember this device for 30 days
            </span>
          </label>

          {/* Info Text */}
          <p className="text-xs mt-8 text-center" style={{ color: 'var(--t-text-muted)' }}>
            Only authorized administrators can access this application.
            <br />
            Contact your admin for access.
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:flex-1 login-bg relative">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(44,76,59,0.75), rgba(44,76,59,0.5))' }}></div>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <Home className="w-10 h-10 mb-4 opacity-70" strokeWidth={1.5} />
          <h2 className="text-2xl font-semibold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Streamline Your Transactions
          </h2>
          <p className="text-white/70 text-sm max-w-md" style={{ fontFamily: "'Figtree', sans-serif" }}>
            Automated workflows, dynamic deadline tracking, and smart notifications 
            keep your real estate transactions on track.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
